// src/context/SeasonContext.js
import React, { createContext, useContext } from "react";
import { HAZARD_VISIBILITY } from "./HazardVisibility";

const SeasonContext = createContext(null);

const SEASONS = [
  {
    id: "hurricane",
    label: "Hurricane Season",
    startMonthDay: "06-01",
    endMonthDay: "11-30",
    hazardKey: "Hurricane",
    isPrimary: true,
  },
  {
    id: "wildfire",
    label: "Wildfire Season",
    startMonthDay: "02-15",
    endMonthDay: "10-31",
    hazardKey: "Wildfire",
  },
  {
    id: "winterWeather",
    label: "Winter Weather Season",
    startMonthDay: "11-01",
    endMonthDay: "03-20",
    hazardKey: "Winter Weather",
  },
{
    id: "severeStorms",
    label: "Severe Storm Season",
    startMonthDay: "03-01",
    endMonthDay: "06-30",
    hazardKey: "Severe Storms",
    isPrimary: true,
  },
  {
    id: "tornado",
    label: "Tornado Season",
    startMonthDay: "03-01",
    endMonthDay: "06-30",
    hazardKey: "Tornado",
    isPrimary: true, // or false, depending on UX
  },
  {
    id: "flooding",
    label: "Flooding Season",
    startMonthDay: "03-01",
    endMonthDay: "07-31",
    hazardKey: "Flooding",
  },
  {
    id: "surfRip",
    label: "Surf & Rip Current Season",
    startMonthDay: "05-01",
    endMonthDay: "09-30",
    hazardKey: "Surf & Rip Currents",
  },
];

const mdToNumber = (monthDay) => {
  const [m, d] = monthDay.split("-").map((n) => parseInt(n, 10));
  // e.g. 11-01 -> 1101, 03-31 -> 331
  return m * 100 + d;
};

const isInRange = (today, startMonthDay, endMonthDay) => {
  const m = today.getMonth() + 1; // 1–12
  const d = today.getDate();
  const todayNum = m * 100 + d;

  const startNum = mdToNumber(startMonthDay);
  const endNum = mdToNumber(endMonthDay);

  // Non wrap-around (e.g., Mar–Jul)
  if (startNum <= endNum) {
    return todayNum >= startNum && todayNum <= endNum;
  }

  // Wrap-around season (e.g., Nov–Mar)
  return todayNum >= startNum || todayNum <= endNum;
};

// Only include seasons whose hazardKey is visible.
// Using === true so visibility must be explicitly turned on.
const visibleSeasons = SEASONS.filter(
  (s) => HAZARD_VISIBILITY[s.hazardKey] === true
);

// When multiple seasons overlap, pick the “newer” one:
// the one whose start date is closest in the past.
const pickDefaultSeason = (today, seasons) => {
  if (seasons.length === 0) return null;
  if (seasons.length === 1) return seasons[0];

  const todayNum = (today.getMonth() + 1) * 100 + today.getDate();

  const distanceFromStart = (season) => {
    const startNum = mdToNumber(season.startMonthDay);
    const endNum = mdToNumber(season.endMonthDay);

    // Non wrap-around season
    if (startNum <= endNum) {
      return todayNum - startNum;
    }

    // Wrap-around season (e.g., winter: 11-01 to 03-31)
    // If we're in the early-year segment (Jan–Mar), treat start as previous year.
    if (todayNum <= endNum) {
      return todayNum + 1200 - startNum;
    }

    // Late-year segment (Nov–Dec)
    return todayNum - startNum;
  };

  // Smaller positive distanceFromStart = more recently started = “newer”
  return seasons
    .slice()
    .sort((a, b) => distanceFromStart(a) - distanceFromStart(b))[0];
};

export const SeasonProvider = ({ children }) => {
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    12,
    0,
    0,
    0
  );

  const currentSeasons = visibleSeasons.filter((s) =>
    isInRange(today, s.startMonthDay, s.endMonthDay)
  );

  const defaultSeason = pickDefaultSeason(today, currentSeasons);

  console.log(
    "[SeasonContext] today (local noon):",
    today.toISOString(),
    "md:",
    (today.getMonth() + 1) * 100 + today.getDate(),
    "currentSeasons:",
    currentSeasons.map((s) => s.id),
    "defaultSeason:",
    defaultSeason?.id
  );

  const value = {
    seasons: visibleSeasons,
    currentSeasons,
    defaultSeason, // <- use this as your default selection in the UI
    upcomingSeasons: [],
  };

  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};

export const useSeasons = () => {
  const ctx = useContext(SeasonContext);
  if (!ctx) throw new Error("useSeasons must be used within SeasonProvider");
  return ctx;
};
